import { removeAllWhitespace } from "./strings"

/**
 * Formats dice notation string by removing whitespace, converting to uppercase,
 * and ensuring proper order (dice notation before modifier)
 * @param input - Raw dice notation string (e.g., "2d6+3", "5+d6", "user")
 * @returns Formatted dice notation string with consistent formatting
 * @example
 * formatDiceNotation("2d6+3") // "2D6+3"
 * formatDiceNotation("5+d6") // "D6+5"
 * formatDiceNotation("user") // "User"
 */
function formatDiceNotation(input: string): string {
  // Remove whitespace and convert to uppercase.
  const trimmedInput = removeAllWhitespace(input).toUpperCase()
  // Try to split string at "+".
  const subStrArray = trimmedInput.split("+")
  // Return trimmed string if there was no "+".
  if (subStrArray.length < 2) {
    if (trimmedInput === "USER") {
      return "User"
    }
    return trimmedInput
  }
  // Check if value before "+" is a number.
  if (isNaN(Number(subStrArray[1]))) {
    // Value after "+" is not a number.
    // E.g. "3+D3"
    // Add the "+" back in but swap the values either side of the operator.
    // E.g. "D3+3"
    return `${subStrArray[1]}+${subStrArray[0]}`
  }
  return trimmedInput
}

/**
 * Formats a number with thousands separators
 * @param num - Number to format
 * @param locale - Locale for formatting (defaults to en-US)
 * @returns Formatted number string
 */
function formatThousandsSeparator(
  num: number,
  locale: string = "en-US"
): string {
  return new Intl.NumberFormat(locale, { style: "decimal" }).format(num)
}

/**
 * Converts numbers to padded strings for easier sorting
 * @param num - Number to convert
 * @returns Padded string representation
 * @example
 * numToPaddedString(2) // "02"
 * numToPaddedString(10) // "10"
 * numToPaddedString(0) // "0"
 */
function numToPaddedString(num: number): string {
  // Converts numbers to padded strings for easier sorting.
  // E.g.
  // 2 -> "002"
  // 10 -> "010"
  // 0 -> "0"
  if (num === 0) {
    // Don't pad zeros.
    return "0"
  }

  if (num < 0) {
    // Handle negative numbers by padding the absolute value and adding the minus sign back
    return `-${String(Math.abs(num)).padStart(2, "0")}`
  }

  return String(num).padStart(3, "0")
}

export { formatDiceNotation, formatThousandsSeparator, numToPaddedString }
